import { useId, useState } from 'react';
import { format, parse } from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { Button } from './Button.js';
import { Calendar } from './Calendar.js';
import { Popover, PopoverContent, PopoverTrigger } from './Popover.js';

/** Renders/parses the app's `YYYY-MM-DD` date strings (see IsoDateSchema) as a shadcn-style
 * popover date picker instead of the native `<input type="date">`. */
export function DatePicker({
  id,
  value,
  onChange,
  onClear,
  placeholder = 'Pick a date',
  disabled,
  className,
}: {
  id?: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  /** When provided, shows a clear (×) affordance that resets the value to null. */
  onClear?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const autoId = useId();
  const selected = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined;
  const isValid = selected && !Number.isNaN(selected.getTime());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button
            id={id ?? autoId}
            type="button"
            variant="secondary"
            disabled={disabled}
            className={cn(
              'h-9 w-full justify-start border-border bg-bg px-3 font-normal text-text hover:border-[var(--ink-400)]',
              !isValid && 'text-muted',
              onClear && isValid && 'pr-8',
              className,
            )}
          >
            <CalendarIcon className="h-4 w-4 opacity-60" />
            {isValid ? format(selected, 'PP') : placeholder}
          </Button>
        </PopoverTrigger>
        {onClear && isValid && (
          <button
            type="button"
            aria-label="Clear date"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-text"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent align="start">
        <Calendar
          mode="single"
          selected={isValid ? selected : undefined}
          defaultMonth={isValid ? selected : undefined}
          onSelect={(date) => {
            if (!date) return;
            onChange(format(date, 'yyyy-MM-dd'));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
